import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { testIds } from "@/constants/testIds";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const WEEKDAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function CalendarView() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSelectedDay(null);
    api
      .get(`/calendar/occupancy?year=${year}&month=${month}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [year, month]);

  const prev = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else setMonth(month - 1);
  };
  const next = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else setMonth(month + 1);
  };

  const firstDayJs = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const firstDayISO = (firstDayJs + 6) % 7; // 0=Mon
  const ndays = data?.days?.length || 0;

  const cells = [];
  for (let i = 0; i < firstDayISO; i++) cells.push(null);
  if (data) for (const d of data.days) cells.push(d);

  const selected = selectedDay
    ? data?.days.find((d) => d.date === selectedDay)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          data-testid={testIds.adminCalPrev}
          onClick={prev}
          className="w-10 h-10 flex items-center justify-center rounded-lg bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 transition"
          aria-label="Mese precedente"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-center">
          <div className="font-heading text-lg sm:text-2xl font-black uppercase tracking-tight text-white">
            {MONTHS_IT[month - 1]}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            {year}
          </div>
        </div>
        <button
          data-testid={testIds.adminCalNext}
          onClick={next}
          className="w-10 h-10 flex items-center justify-center rounded-lg bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 transition"
          aria-label="Mese successivo"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-zinc-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-1">
            {WEEKDAYS_IT.map((d) => (
              <div key={d} className="py-1">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell, i) => {
              if (!cell) return <div key={i} className="aspect-square" />;
              const dayNum = parseInt(cell.date.slice(-2), 10);
              const isSelected = selectedDay === cell.date;
              const isToday =
                cell.date === today.toISOString().slice(0, 10);
              return (
                <button
                  key={cell.date}
                  data-testid={testIds.adminCalDay(dayNum)}
                  onClick={() =>
                    setSelectedDay(isSelected ? null : cell.date)
                  }
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm font-mono font-bold border transition-all active:scale-95 ${
                    cell.occupied
                      ? "bg-rose-600/25 border-rose-500/50 text-rose-200 hover:bg-rose-600/40"
                      : "bg-emerald-600/10 border-emerald-500/20 text-emerald-300/80 hover:bg-emerald-600/20"
                  } ${isSelected ? "ring-2 ring-rose-400 ring-offset-2 ring-offset-zinc-950" : ""} ${isToday ? "border-white/50" : ""}`}
                >
                  <span>{dayNum}</span>
                  {cell.occupied && (
                    <span className="text-[9px] font-normal opacity-80">
                      {cell.quotes.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-4 pt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded bg-emerald-600/40 border border-emerald-500/40" />{" "}
              Libero
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded bg-rose-600/50 border border-rose-500/50" />{" "}
              Occupato
            </span>
            <span className="ml-auto text-zinc-600">
              Solo preventivi confermati
            </span>
          </div>

          {selected && selected.occupied && (
            <div className="mt-4 bg-zinc-900/90 border border-rose-900/50 rounded-xl p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-rose-400 mb-2">
                Occupazione del {selected.date}
              </div>
              <div className="space-y-2">
                {selected.quotes && selected.quotes.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                      Preventivi confermati
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.quotes.map((q) => (
                        <span
                          key={q.id}
                          className="px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-xs text-zinc-200 break-words"
                        >
                          {q.customer_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selected.blocks && selected.blocks.length > 0 && (
                  <div className="pt-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                      Blocchi manuali
                    </div>
                    <div className="space-y-1">
                      {selected.blocks.map((b) => (
                        <div
                          key={b.id}
                          className="text-xs text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 flex justify-between gap-2 flex-wrap"
                        >
                          <span className="font-mono break-words min-w-0 flex-1">
                            {b.item_name} × {b.quantity}
                          </span>
                          {b.reason && (
                            <span className="text-zinc-500 text-[10px]">
                              {b.reason}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="pt-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Articoli occupati (totale)
                  </div>
                  <div className="space-y-1">
                    {selected.items.map((it, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between gap-2 text-xs text-zinc-300 font-mono border-b border-zinc-800/60 py-1"
                      >
                        <span className="break-words min-w-0 flex-1">
                          {it.name}
                        </span>
                        <span className="text-rose-400 shrink-0">
                          × {it.qty}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
